import { Application } from '../models/view/Application';

/**
 * Application session handling and basic storage access.
 *
 */
export class BasicPersistenceService<T extends BasicAppDefinition> {
	public static readonly DEFAULT_IBAN_SESSION_KEYPREFIX = "application.iban";
	public static readonly DEFAULT_ADVERTISING_CONSENT_SESSION_KEYPREFIX = "application.advertisingconsent";
	public static readonly DEFAULT_APPLICATION_SESSION_KEY = "application.init";
	public static readonly VOUCHERCODE_SESSION_KEY = "voucherCode";
	public static readonly VOUCHERMAIL_SESSION_KEY = "voucherMail";

	protected readonly options: BasicAppOptions<T>;
	protected applicationSessionKey: string = BasicPersistenceService.DEFAULT_APPLICATION_SESSION_KEY;
	protected advertisingConsentSessionKey: string = BasicPersistenceService.DEFAULT_ADVERTISING_CONSENT_SESSION_KEYPREFIX;

	/**
	 * Creates an instance of PersistenceService.
	 * @param options - application options.
	 */
	constructor(options: BasicAppOptions<T>) {
		this.options = options;
	}

	public hasApplicationSession(): boolean {
		const applicationModel = this.getApplicationModel();
		if (!applicationModel || (applicationModel.Register !== this.options.name)) {
			return false;
		}
		return true;
	}

	public getLastPageUrl(): string {
		const lastVisitedPage: any = T.Utils.Store.get("lastVisitedPage", T.Utils.Store.SESSION);
		return lastVisitedPage ? (lastVisitedPage.href || lastVisitedPage.toString()) : undefined;
	}

	/**
	 * If a voucher code has been entered, it will be returned, otherwise an empty string will be returned
	 */
	public getVoucherCode(keepInSession = true): string {
		const voucherCode = T.Utils.Store.get(BasicPersistenceService.VOUCHERCODE_SESSION_KEY, T.Utils.Store.SESSION);
		if (!keepInSession) {
			this.set(BasicPersistenceService.VOUCHERCODE_SESSION_KEY, null);
		}
		return voucherCode || '';
	}

	/**
	 * If a voucher mail has been entered, it will be returned and deleted, otherwise an empty string will be returned.
	 */
	public getVoucherMail(): string {
		const voucherMail = T.Utils.Store.get(BasicPersistenceService.VOUCHERMAIL_SESSION_KEY, T.Utils.Store.SESSION);
		if (voucherMail) {
			this.set(BasicPersistenceService.VOUCHERMAIL_SESSION_KEY, null);
		}
		return voucherMail || '';
	}

	/**
	 * Initializes the application session, if needed.
	 * Ensures that data between different applications don't get mixed.
	 */
	public prepareApplicationSession(): void {
		const applicationModel = this.getApplicationModel();
		if (!applicationModel || applicationModel.Register !== this.options.name) {
			this.clearApplicationSession();
			this.setApplicationModel(
				new Application(
					this.options.name,
					this.options.app.membershipadvertisingkey
				)
			);
		}
	}

	/**
	 * Persists the provided application model.
	 */
	public setApplicationModel(applicationModel: Application): void {
		this.set(this.applicationSessionKey, applicationModel);
	}

	/**
	 * Gets the generic application model that should be converted to an application specific model.
	 *
	 * @return Generic application model. Should be overwritten to get application specific model.
	 */
	public getApplicationModel(): Application {
		return Application.createFromObject(this.getApplicationObject());
	}

	protected getApplicationObject(): { [key: string]: any } {
		return T.Utils.Store.get(this.applicationSessionKey, T.Utils.Store.SESSION);
	}

	private clearApplicationModel(): void {
		this.set(this.applicationSessionKey, null);
	}

	private clearAdvertisingConsentSessionStorage(): void {
		T.Utils.Store.set(this.advertisingConsentSessionKey, null, T.Utils.Store.VARIABLE);
	}

	/**
	 * Clears all standard session data ('application.init', 'customer').
	 * Should be called after the user completed an application with either a success page or an error page.
	 */
	public clearApplicationSession(): void {
		this.clearApplicationModel();
		this.clearIbanSessionStorage();
		this.clearAdvertisingConsentSessionStorage();
	}

	private clearIbanSessionStorage(): void {
		// to use T.Utils.Store.keys() directly without Array.from either downlevelIteration=true or target=es2015 must be defined in tsconfig
		for (const key of Array.from(T.Utils.Store.keys(T.Utils.Store.SESSION))) {
			if (key.indexOf(BasicPersistenceService.DEFAULT_IBAN_SESSION_KEYPREFIX) !== -1) {
				this.set(key, null);
			}
		}
		this.set('customer', null);
		this.set('Iban', null);
	}

	protected set(key: string, value: any): void {
		T.Utils.Store.set(key, value, T.Utils.Store.SESSION);
	}
}
